import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Tooltip } from "flowbite-react";
import {
  HiChevronLeft,
  HiChevronRight,
  HiOutlineInformationCircle,
  HiRefresh,
} from "react-icons/hi";
import { Button, LGBDatepicker } from "@components";
import { useDriverTimeEntries } from "../../hooks/useDriverTimeEntries";
import { DriverTimeEntriesTable } from "./DriverTimeEntriesTable";
import {
  moveTimeEntryWeek,
  overlappingTimeEntries,
  timeEntryWeek,
  todayInOslo,
} from "./timeEntries";

export function DriverTimeEntries({
  workspaceId,
  driverId,
}: {
  workspaceId: string;
  driverId: string;
}) {
  const { t } = useTranslation("driver");
  const [date, setDate] = useState(todayInOslo);
  const [choosingWeek, setChoosingWeek] = useState(false);
  const week = timeEntryWeek(date);
  const query = useDriverTimeEntries(workspaceId, driverId, week.from, week.to);
  const rows = query.isError ? [] : (query.data?.entries ?? []);
  const overlaps = overlappingTimeEntries(rows);
  const total = rows.reduce(
    (sum, row) => sum + (row.voided ? 0 : row.periodSeconds),
    0,
  );
  const totalMinutes = Math.round(Math.max(0, total) / 60);
  return (
    <div className="space-y-4 p-4 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex gap-1">
            <Button
              variant="secondary"
              size="sm"
              aria-label={t("profile.previous_week")}
              icon={<HiChevronLeft />}
              iconPosition="only"
              onClick={() => setDate(moveTimeEntryWeek(date, -1))}
            />
            <Button
              variant="secondary"
              size="sm"
              aria-label={t("profile.next_week")}
              icon={<HiChevronRight />}
              iconPosition="only"
              onClick={() => setDate(moveTimeEntryWeek(date, 1))}
            />
          </div>
          <div>
            <button
              type="button"
              className="text-lg font-semibold text-text hover:text-brand focus-visible:outline-brand"
              aria-label={t("profile.choose_week")}
              aria-expanded={choosingWeek}
              onClick={() => setChoosingWeek(!choosingWeek)}
            >
              {t("profile.week_number", { number: week.number })}
            </button>
            <p className="text-sm text-secondary">{week.label} · Europe/Oslo</p>
          </div>
        </div>
        {!query.isLoading && !query.isError && (
          <div className="flex items-center gap-3">
            <Button
              variant="secondary"
              size="sm"
              aria-label={t("profile.refresh")}
              title={t("profile.refresh")}
              icon={<HiRefresh />}
              iconPosition="only"
              onClick={() => query.refetch()}
              disabled={query.isFetching}
            />
            {query.data?.accountLinked !== false && (
              <div className="sm:text-right">
                <div className="flex items-center gap-1 text-sm text-secondary sm:justify-end">
                  {t("profile.total")}
                  <Tooltip content={t("profile.total_hint")}>
                    <button
                      type="button"
                      aria-label={t("profile.total_hint")}
                      className="inline-flex p-1"
                    >
                      <HiOutlineInformationCircle aria-hidden="true" />
                    </button>
                  </Tooltip>
                </div>
                <p className="text-2xl font-semibold tabular-nums text-text">
                  {t("profile.total_duration", {
                    hours: Math.floor(totalMinutes / 60),
                    minutes: totalMinutes % 60,
                  })}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
      {choosingWeek && (
        <div className="w-64 max-w-full">
          <label htmlFor="driver-time-week" className="sr-only">
            {t("profile.choose_week_date")}
          </label>
          <LGBDatepicker
            inputId="driver-time-week"
            selectedDate={date}
            select={(value) => {
              setDate(value);
              setChoosingWeek(false);
            }}
            allowPastDates
            maxDate={null}
            overlayCalendar
          />
        </div>
      )}
      {query.isLoading ? (
        <p role="status">{t("profile.loading")}</p>
      ) : query.isError ? (
        <div className="space-y-3">
          <p role="alert" className="text-danger">
            {t("profile.time_error")}
          </p>
          <Button
            variant="secondary"
            onClick={() => query.refetch()}
            disabled={query.isFetching}
          >
            {t("profile.refresh")}
          </Button>
        </div>
      ) : (
        <>
          {overlaps.size > 0 && (
            <p className="text-sm text-warning" role="status">
              {t("profile.overlap_hint")}
            </p>
          )}
          {!rows.length ? (
            <p className="text-sm text-muted">
              {t(
                query.data?.accountLinked === false
                  ? "profile.no_account"
                  : "profile.no_entries",
              )}
            </p>
          ) : (
            <DriverTimeEntriesTable
              entries={rows}
              overlaps={overlaps}
              total={total}
            />
          )}
        </>
      )}
    </div>
  );
}
